import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Zap, Layers, Palette, Ghost, Wand2 } from 'lucide-react';

const features = [
  { icon: Zap, title: 'Zero Sign-Up', body: 'Open the studio, pick your look, copy a pro prompt. No login, no payment, forever free.' },
  { icon: Layers, title: 'Six Studios · One Canvas', body: 'Quick Create, Advanced Typography, Per-Letter Mixer, Holidays + Topics, Supporting Elements, Full Composition.' },
  { icon: Palette, title: '500+ Seeded Controls', body: 'Fonts, materials, patterns, finishes, layouts, effects — plus custom entry beneath every dropdown.' },
  { icon: Ghost, title: 'Holidays For Days', body: 'Halloween, Christmas, Valentine, Easter, Mother\'s Day, 4th of July, Thanksgiving — all deep, all editable.' },
  { icon: Wand2, title: 'Master + 5 Variations', body: 'One click compiles a production-grade master prompt plus five meaningfully different variations.' },
  { icon: Sparkles, title: 'Local & Portable', body: 'Projects auto-save in your browser. Export any recipe as JSON to jump between devices.' },
];

export default function Home() {
  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 relative z-10" data-testid="home-page">
      <div className="text-center max-w-3xl mx-auto">
        <div className="eyebrow mb-4">Free · No Login · Unlimited</div>
        <h1 className="font-display text-5xl sm:text-7xl leading-none tracking-tight">
          Build <span className="italic">production-grade</span><br />text prompts in <span style={{ color: '#FF107A' }}>60 seconds</span>.
        </h1>
        <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
          TYPE ELITE is a deep prompt-building studio for any AI image tool — Gemini, ChatGPT, Midjourney, Ideogram. Style whole phrases, single letters, holiday drops, flyers, tees or logos. Copy the prompt and paste anywhere.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/studio" data-testid="cta-start" className="btn-magenta rounded-full px-8 py-3 text-sm uppercase tracking-[0.24em]">Start Creating</Link>
          <Link to="/presets" data-testid="cta-presets" className="rounded-full px-8 py-3 text-sm uppercase tracking-[0.24em] chrome-border chrome-hover">Browse Presets</Link>
        </div>
      </div>

      <div className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="rounded-2xl chrome-border bg-zinc-900/60 p-6 hover:border-pink-500/40 transition-colors fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <Icon size={22} className="text-pink-500 mb-4" />
              <h3 className="font-display text-2xl">{f.title}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.body}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-24 rounded-2xl chrome-border p-8 sm:p-12 bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 text-center">
        <div className="eyebrow mb-3">The Workflow</div>
        <h2 className="font-display text-4xl">Text · Style · Copy · Paste</h2>
        <p className="mt-3 text-zinc-400 max-w-xl mx-auto">Type your exact wording. Pick a font family, material, finish, layout, effect. Add holiday flavor. Hit generate. Paste the prompt into your favorite AI image tool.</p>
        <Link to="/studio" data-testid="cta-start-2" className="btn-magenta rounded-full px-8 py-3 text-sm uppercase tracking-[0.24em] inline-block mt-6">Open The Studio</Link>
      </div>
    </main>
  );
}
